import dynamic from 'next/dynamic'
import { ButtonHTMLAttributes, FC, HTMLAttributes, PropsWithChildren, ReactNode, useState } from 'react'

import { useLanguage, useLayout } from '@/hooks/Contexts'
import { useScrollable } from '@/hooks/Index'
import { DiscountItem } from '@/models/Index'
import { svgs } from '@/utils/Images'
import { Action0 } from '@/utils/Types'

import { ModalVideoDrawPlayer } from './Layouts/Index'
import LoadingSpinner from './LoadingSpinner'

const NumberInput = dynamic(() => import('./FormElement/NumberInput'))

interface ModalProps {
  show: boolean
  onClose?: Action0
  outerCls?: string
  innerProps?: HTMLAttributes<HTMLDivElement>
  title?: ReactNode
  titleCls?: string
  bodyCls?: string
}
export const ModalContainer: FC<PropsWithChildren<ModalProps>> = ({ show, onClose, outerCls, innerProps, title, titleCls, children, bodyCls }) => {
  useScrollable(show)
  // when hidden the modal stays in the DOM for the fade animation, so remove it
  // from the keyboard tab order + a11y tree (otherwise its controls stay tabbable)
  const inertWhenClosed = show ? {} : ({ inert: '' } as Record<string, string>)
  return (
    <div
      {...inertWhenClosed}
      aria-hidden={!show}
      className={`fixed left-0 top-0 z-50 flex h-screen w-screen items-center transition-all duration-500 ${
        show ? 'translate-y-[10px] opacity-100' : 'pointer-events-none -translate-y-[10px] opacity-0'
      } ${outerCls || ''}`}
      aria-labelledby='modal'
      role='dialog'
      aria-modal='true'
    >
      <div className='fixed inset-0 bg-stone-900/70' aria-hidden='true' />
      <div {...innerProps} className={`relative mx-4 w-full rounded-lg bg-white/100 shadow md:mx-auto ${innerProps?.className || ''}`}>
        <div className={`flex w-full items-center justify-end px-4 py-2 ${titleCls || ''}`}>
          {title}
          <span onClick={onClose} className='h-4 w-4 cursor-pointer hover:text-amber-500'>
            {svgs.cross}
          </span>
        </div>
        <div className={`px-4 pb-4 md:px-6 md:pb-6 ${bodyCls || ''}`}>{children}</div>
      </div>
    </div>
  )
}

export type ModalWithImageProps = {
  type?: 'success' | 'image' | 'warning'
  imgUrl?: string
  content?: string
  title?: string
  alt?: string
  onConfirm?: Action0
}
export const ModalWithImage: FC<ModalProps & ModalWithImageProps> = ({ show, onClose, onConfirm, type, imgUrl, title, alt, content, children }) => {
  const { competitions: compStr, cart: cartStr, actions: actStr } = useLanguage()

  return (
    <ModalContainer
      show={show}
      onClose={onClose}
      title={type == 'success' ? <div className='mx-auto text-center font-medium capitalize text-black'>{cartStr.congratulations}</div> : null}
      innerProps={{ className: type == 'image' ? 'md:w-1/2 xl:max-w-[480px]' : 'md:w-1/2 lg:w-96' }}
    >
      {/* Image */}
      <div className={`flex w-full min-w-fit justify-center ${type === 'success' ? 'rounded-xl bg-cyan-v2' : 'h-auto'}`}>
        {type == 'image' ? (
          imgUrl && <img src={imgUrl} alt={alt ?? imgUrl} className='h-auto max-h-80 w-full rounded object-fill' />
        ) : type == 'success' ? (
          <svg className='my-8 w-1/4 fill-white/100' viewBox='0 0 330 330'>
            {svgs.check}
          </svg>
        ) : type == 'warning' ? (
          <svg className='my-8 w-1/4 fill-white/100' viewBox='0 0 330 330' stroke='currentColor' strokeWidth={2}>
            {svgs.warning}
          </svg>
        ) : null}
      </div>

      {/* Main content */}
      <div className='mt-4 text-center'>
        {type == 'image' && title && <p className='mb-2 text-base font-semibold md:text-lg'>{title}</p>}
        <p className='text-sm md:text-base' data-testid={type == 'success' ? 'txt-competition-addedbasket' : null}>
          {content}
        </p>
      </div>
      <div className='mt-4 grid w-full min-w-max grid-cols-2 gap-4'>
        <button className='btn-rd-outline btn-hover-amber h-10' onClick={onClose} data-testid={type == 'success' ? '' : null}>
          {type == 'image' ? compStr.maybe_later : type == 'warning' ? cartStr.let_me_think : cartStr.keep_shopping}
        </button>
        <button className='btn-rd-amber btn-hover-amber h-10' onClick={onConfirm} data-testid={type == 'success' ? 'btn-addCart-chkOut' : null}>
          {type == 'image' ? compStr.details : type == 'warning' ? actStr.confirm : cartStr.checkout}
        </button>
      </div>
      {children}
    </ModalContainer>
  )
}

interface ConfirmModalProps extends ModalProps {
  content?: string
  confirmProps?: ButtonHTMLAttributes<HTMLButtonElement>
  showCloseBtn?: boolean
  tip?: boolean
}
export const ConfirmModal: FC<ConfirmModalProps> = ({ onClose, title, content, confirmProps, showCloseBtn, tip, ...rest }) => {
  const { actions: actStr } = useLanguage()
  return (
    <ModalContainer {...rest} onClose={onClose} innerProps={{ className: 'lg:w-3/5 md:w-2/3 2xl:w-2/5' }}>
      <div className='mb-4 flex w-full flex-col'>
        {tip ? (
          <p className='text-center text-lg font-semibold lg:text-xl'>{title}</p>
        ) : (
          <div className='flex'>
            <svg className='mr-2 mt-1 h-5 w-5 text-warning md:mr-4' viewBox='0 0 24 24' width='100%' height='100%' fill='currentColor'>
              <path d='M23.32 17.191L15.438 2.184C14.728.833 13.416 0 11.996 0c-1.42 0-2.733.833-3.443 2.184L.533 17.448a4.744 4.744 0 000 4.368C1.243 23.167 2.555 24 3.975 24h16.05C22.22 24 24 22.044 24 19.632c0-.904-.251-1.746-.68-2.44zm-9.622 1.46c0 1.033-.724 1.823-1.698 1.823s-1.698-.79-1.698-1.822v-.043c0-1.028.724-1.822 1.698-1.822s1.698.79 1.698 1.822v.043zm.039-12.285l-.84 8.06c-.057.581-.408.943-.897.943-.49 0-.84-.367-.896-.942l-.84-8.065c-.057-.624.25-1.095.779-1.095h1.91c.528.005.84.476.784 1.1z'></path>
            </svg>
            <p className='mb-2 text-lg font-semibold lg:text-xl'>{title}</p>
          </div>
        )}
        <p className='whitespace-pre-wrap'>{content}</p>
      </div>
      {tip ? (
        <div className='mt-4 flex w-full justify-center'>
          <button className='btn-primary w-full max-w-[240px] !rounded py-3 font-semibold capitalize' onClick={confirmProps?.onClick} type='button'>
            {tip}
          </button>
        </div>
      ) : (
        <div className='mt-8 flex w-full flex-row-reverse gap-x-4'>
          {confirmProps && (
            <button {...confirmProps} className='btn-rd-outline btn-hover-amber h-10 px-4' type='button'>
              {confirmProps.children || actStr.confirm}
            </button>
          )}
          {onClose && showCloseBtn && (
            <button className='btn-rd-amber btn-hover-amber h-10 px-4' onClick={onClose} type='button'>
              {actStr.close}
            </button>
          )}
        </div>
      )}
    </ModalContainer>
  )
}

interface IframeModalProps extends ModalProps {
  url?: string
  iframe?: string
}
export const IframeModal: FC<IframeModalProps> = ({ url, iframe, show, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPlayerIFrame, setIsPlayerIFrame] = useState(false)
  const close = () => {
    if (onClose) {
      onClose()
      setIsPlaying(false)
    }
  }
  return (
    <ModalContainer show={show} onClose={close} innerProps={{ className: 'h-auto w-full md:w-2/3 lg:w-1/2' }}>
      {(url || iframe) && (show || !isPlayerIFrame) && (
        <ModalVideoDrawPlayer url={url} iframe={iframe} isPlaying={isPlaying} toggleIsPlayerIFrame={setIsPlayerIFrame} toggleIsPlaying={setIsPlaying} />
      )}
    </ModalContainer>
  )
}

// Quick Picks Modal Types
export interface QuickPickItem {
  id: number
  image: string
  title: string
  price: number
  quantity?: number
  added_quantity?: number
  max_quantity?: number
  recently_added?: boolean
  loading?: boolean
  quantity_discounts?: DiscountItem[]
}

export interface QuickPicksModalProps {
  items: QuickPickItem[]
  onQuantityChange?: any
  onQuickAdd?: any
  onViewCart?: Action0
}

export const QuickPicksModal: FC<ModalProps & QuickPicksModalProps> = ({ show, onClose, items, onQuantityChange, onQuickAdd, onViewCart }) => {
  const { cart: cartStr } = useLanguage()
  const { isDesktop } = useLayout()

  const getDiscountPrice = (id: number) => {
    const item = items.find(item => item.id === id)
    if (!item) return 0
    const discount = item.quantity_discounts
      ?.slice()
      .reverse()
      .find(discount => item.quantity >= discount.qty)
    return discount ? (((100 - discount.discount_rate) * item.price) / 100) * (item.quantity || 1) : item.price * (item.quantity || 1)
  }

  return (
    <ModalContainer
      show={show}
      onClose={onClose}
      title={<div className='mx-auto my-2 text-center text-xl capitalize text-black'>{cartStr.quick_picks.title}</div>}
      innerProps={{ className: 'md:w-3/4 lg:w-2/3 xl:max-w-[600px]' }}
    >
      {/* Quick Picks Items */}
      <hr className='mb-2 border-gray-300' />
      <div className='space-y-0'>
        {items.map(item => {
          const isRecentlyAdded = item.recently_added

          return (
            <div key={item.id}>
              <div className='flex flex-col flex-wrap p-3'>
                {!isDesktop && <p className='w-full text-sm text-gray-900'>{item.title}</p>}
                <div className='flex items-end gap-4'>
                  {/* Image */}
                  <div className={`w-1/3 flex-shrink-0 ${!isDesktop ? 'mt-2' : ''}`}>
                    <img src={item.image} alt={item.title} className='w-full rounded object-fill' />
                  </div>

                  {/* Content */}
                  <div className={`flex w-2/3 min-w-0 flex-1 flex-col ${isDesktop ? 'gap-4' : 'gap-2'}`}>
                    {isDesktop && <p className='w-full text-sm text-gray-900'>{item.title}</p>}
                    <div className={`flex flex-wrap ${isDesktop ? 'justify-between' : 'justify-start'} gap-2`}>
                      <div className={`flex items-center gap-2 ${!isDesktop ? 'w-full' : 'w-fit'}`}>
                        {(() => {
                          const hasDiscount = item.quantity_discounts?.some(discount => item.quantity >= discount.qty && discount.discount_rate > 0)
                          const discountPrice = hasDiscount
                            ? ((100 -
                                (item.quantity_discounts
                                  ?.slice()
                                  .reverse()
                                  .find(discount => item.quantity >= discount.qty)?.discount_rate || 0)) *
                                item.price) /
                              100
                            : item.price

                          return (
                            <>
                              <span className={`text-lg font-bold text-cyan-v2`}>
                                {Number(discountPrice) < 1 ? (Number(discountPrice) * 100).toFixed(0) + 'p' : '£ ' + Number(discountPrice)?.toFixed(2)}
                              </span>
                              {hasDiscount && (
                                <>
                                  <span className='text-sm text-gray-500 line-through'>
                                    {Number(item.price) < 1 ? (Number(item.price) * 100).toFixed(0) + 'p' : '£ ' + Number(item.price)?.toFixed(2)}
                                  </span>
                                  <span className='text-sm text-gray-500'>
                                    {/* get integer if discount_rate is integer */}
                                    {(() => {
                                      const discountStr = item.quantity_discounts
                                        ?.slice()
                                        .reverse()
                                        .find(d => item.quantity >= d.qty)?.discount_rate

                                      if (discountStr != null) {
                                        const discount = Number(discountStr) // convert to number
                                        return Number.isInteger(discount) ? discount : discount.toFixed(0)
                                      }
                                      return null
                                    })()}
                                    % off
                                  </span>
                                </>
                              )}
                            </>
                          )
                        })()}
                      </div>
                      {isDesktop && (
                        <div className='w-fit'>
                          {item.added_quantity ? (
                            <div className='flex items-center gap-2 text-sm font-semibold text-gray-900'>
                              <img src='/assets/available.svg' alt='available' className='h-4 w-4' />
                              <span className='text-lg font-normal text-gray-900'>×</span> {item.added_quantity} {cartStr.quick_picks.tickets}
                            </div>
                          ) : (
                            <div className='h-5' />
                          )}
                        </div>
                      )}
                      {!isDesktop && (
                        <NumberInput
                          value={item.quantity || 1}
                          max={item.max_quantity || 99999}
                          onChange={value => onQuantityChange?.(item.id, value)}
                          input={{ min: 1, max: item.max_quantity || 99999 }}
                          className={`h-7 origin-top-left scale-90`}
                        />
                      )}
                    </div>
                    {/* display if pc */}
                    {isDesktop && (
                      <>
                        <div className='flex flex-row flex-wrap items-center justify-between gap-2'>
                          <NumberInput
                            value={item.quantity || 1}
                            max={item.max_quantity || 99999}
                            onChange={value => onQuantityChange?.(item.id, value)}
                            input={{ min: 1, max: item.max_quantity || 99999 }}
                            className={`h-7`}
                          />
                          <button
                            onClick={() => onQuickAdd?.(item.id, item.quantity)}
                            disabled={isRecentlyAdded || item.loading}
                            className={`min-h-8 w-full max-w-[190px] px-4 text-sm ${
                              isRecentlyAdded
                                ? 'cursor-not-allowed rounded-full bg-green-600 font-semibold text-white'
                                : item.loading
                                ? 'cursor-not-allowed rounded-full bg-gray-400 font-semibold text-white'
                                : 'btn-rd-amber btn-hover-amber'
                            }`}
                          >
                            <span className='flex w-full items-center justify-center text-center'>
                              {item.loading && <LoadingSpinner className='mr-2 h-4 w-4 text-white' />}
                              {isRecentlyAdded
                                ? cartStr.quick_picks.tickets_added
                                : item.loading
                                ? cartStr.quick_picks.add_to_basket
                                : cartStr.quick_picks.quick_add.replace('%s', getDiscountPrice(item.id).toFixed(2))}
                            </span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* if not pc */}
                {!isDesktop && (
                  <>
                    <div className='mt-3 flex w-full items-center justify-between'>
                      {/* show if set added quantity else empty div*/}
                      {item.added_quantity ? (
                        <div className='flex w-1/3 gap-1 text-xs text-gray-900'>
                          <img src='/assets/available.svg' alt='available' className='h-3 w-3' />
                          <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M6 6L18 18M6 18L18 6' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
                          </svg>
                          <span className='text-xs font-semibold' style={{ lineHeight: '0.8rem' }}>
                            {item.added_quantity} {cartStr.quick_picks.tickets}
                          </span>
                        </div>
                      ) : (
                        <div className='h-1' />
                      )}
                      <div className='w-2/3 pl-4'>
                        <button
                          onClick={() => onQuickAdd?.(item.id, item.quantity)}
                          disabled={isRecentlyAdded || item.loading}
                          className={`min-h-8 px-4 text-sm ${
                            isRecentlyAdded
                              ? 'w-full cursor-not-allowed rounded-full bg-green-600 font-semibold text-white'
                              : item.loading
                              ? 'w-full cursor-not-allowed rounded-full bg-gray-400 font-semibold text-white'
                              : 'btn-rd-amber btn-hover-amber w-full'
                          }`}
                        >
                          <span className='flex w-full items-center justify-center text-center'>
                            {item.loading && <LoadingSpinner className='mr-2 h-4 w-4 text-white' />}
                            {isRecentlyAdded
                              ? cartStr.quick_picks.tickets_added
                              : item.loading
                              ? cartStr.quick_picks.add_to_basket
                              : cartStr.quick_picks.quick_add.replace('%s', getDiscountPrice(item.id).toFixed(2))}
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <hr className='my-2 border-gray-300' />
            </div>
          )
        })}
      </div>

      {/* Action Buttons */}
      <div className='mt-4 grid w-full grid-cols-2 gap-4'>
        <button className='btn-rd-cyan btn-hover-cyan h-10' onClick={onClose}>
          {cartStr.keep_shopping}
        </button>
        <button className='btn-rd-amber btn-hover-amber h-10' onClick={onViewCart}>
          {cartStr.quick_picks.view_cart}
        </button>
      </div>
    </ModalContainer>
  )
}
